<template>
    <div
        v-if="isAccessChecked && isAdmin"
        class="pending-page"
      >
      <DefaultHeader
        title="Вакансии на модерации"
      />
  
      <main class="pending-content">
        <div class="pending-container">
  
          <!-- =====================================================
               Heading
          ====================================================== -->
  
          <section class="pending-heading">
            <div>
              <span class="pending-label">
                Модерация
              </span>
  
              <h1>
                Вакансии на модерации
              </h1>
  
              <p>
                Проверьте описание, условия и данные вакансии перед публикацией.
              </p>
            </div>
  
            <div class="pending-counter">
              <span class="counter-dot"></span>
  
              <span>
                {{ pendingVacancies.length }}
              </span>
  
              <span class="counter-caption">
                ожидают
              </span>
            </div>
          </section>
  
          <!-- =====================================================
               Loading
          ====================================================== -->
  
          <section
            v-if="isLoading"
            class="vacancies-grid"
          >
            <div
              v-for="index in 4"
              :key="index"
              class="vacancy-card skeleton-card"
            >
              <div class="skeleton-image"></div>
  
              <div class="skeleton-content">
                <div class="skeleton-line small"></div>
                <div class="skeleton-line title"></div>
                <div class="skeleton-line"></div>
                <div class="skeleton-line short"></div>
  
                <div class="skeleton-tags">
                  <div class="skeleton-tag"></div>
                  <div class="skeleton-tag"></div>
                  <div class="skeleton-tag"></div>
                </div>
              </div>
            </div>
          </section>
  
          <!-- =====================================================
               Error
          ====================================================== -->
  
          <section
            v-else-if="errorMessage"
            class="state-card"
          >
            <div class="state-icon error">
              !
            </div>
  
            <h2>
              Не удалось загрузить вакансии
            </h2>
  
            <p>
              {{ errorMessage }}
            </p>
  
            <button
              type="button"
              class="state-button"
              @click="loadPendingVacancies"
            >
              Попробовать снова
            </button>
          </section>
  
          <!-- =====================================================
               Empty
          ====================================================== -->
  
          <section
            v-else-if="pendingVacancies.length === 0"
            class="state-card"
          >
            <div class="state-icon success">
              ✓
            </div>
  
            <h2>
              Всё проверено
            </h2>
  
            <p>
              Сейчас нет вакансий, ожидающих публикации.
            </p>
          </section>
  
          <!-- =====================================================
               Vacancies
          ====================================================== -->
  
          <section
            v-else
            class="vacancies-grid"
          >
            <article
              v-for="vacancy in pendingVacancies"
              :key="vacancy.id"
              class="vacancy-card"
            >
              <!-- Image -->
  
              <div class="vacancy-image-wrap">
                <img
                  v-if="vacancy.image_url"
                  :src="vacancy.image_url"
                  class="vacancy-image"
                  alt=""
                />
  
                <div
                  v-else
                  class="vacancy-image-placeholder"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 7.5C4 6.12 5.12 5 6.5 5H17.5C18.88 5 20 6.12 20 7.5V16.5C20 17.88 18.88 19 17.5 19H6.5C5.12 19 4 17.88 4 16.5V7.5Z"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
  
                    <path
                      d="M7 15L10 12L12 14L15 10L19 14"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
  
                <div class="moderation-badge">
                  На модерации
                </div>
              </div>
  
              <!-- Content -->
  
              <div class="vacancy-body">
  
                <div class="vacancy-topline">
                  <span class="vacancy-category">
                    {{ vacancy.specific?.name || "Вакансия" }}
                  </span>
  
                  <span class="vacancy-date">
                    {{ formatDate(vacancy.date) }}
                  </span>
                </div>
  
                <h2 class="vacancy-title">
                  {{ vacancy.title || "Без названия" }}
                </h2>
  
                <div class="vacancy-meta">
  
                  <div class="meta-item">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M5 20V8L12 4L19 8V20"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M9 20V14H15V20"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />
                    </svg>
  
                    <span>
                      {{ vacancy.company_id || "Компания не указана" }}
                    </span>
                  </div>
  
                  <div class="meta-item">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 21C12 21 18 15.8 18 10.5C18 6.91 15.31 4 12 4C8.69 4 6 6.91 6 10.5C6 15.8 12 21 12 21Z"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />
  
                      <circle
                        cx="12"
                        cy="10"
                        r="2"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />
                    </svg>
  
                    <span>
                      {{ locationTitle(vacancy) }}
                    </span>
                  </div>
  
                  <div
                    v-if="vacancy.salary"
                    class="meta-item"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <rect
                        x="4"
                        y="6"
                        width="16"
                        height="12"
                        rx="2.5"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />
  
                      <path
                        d="M8 12H16"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                      />
                    </svg>
  
                    <span>
                      {{ vacancy.salary }}
                    </span>
                  </div>
                </div>

                <!-- Company -->

                <a
                  v-if="vacancy.company"
                  :href="`/company/${vacancy.company.id}`"
                  class="company-card"
                  target="_blank"
                  rel="noopener"
                >
                  <div class="company-logo">
                    <img
                      v-if="vacancy.company.image_url"
                      :src="vacancy.company.image_url"
                      :alt="vacancy.company.name || 'Компания'"
                    />

                    <span v-else>
                      {{ companyInitial(vacancy.company.name) }}
                    </span>
                  </div>

                  <div class="company-content">
                    <span class="company-label">
                      Компания
                    </span>

                    <span class="company-name">
                      {{ vacancy.company.name || "Без названия" }}
                    </span>

                    <span
                      v-if="vacancy.company.site"
                      class="company-site"
                    >
                      {{ formatCompanySite(vacancy.company.site) }}
                    </span>
                  </div>

                  <div class="company-open">
                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M7 5L12 10L7 15"
                        stroke="currentColor"
                        stroke-width="1.7"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </a>

                <div
                  v-else
                  class="company-card company-card-missing"
                >
                  <div class="company-logo missing">
                    ?
                  </div>

                  <div class="company-content">
                    <span class="company-label">
                      Компания
                    </span>

                    <span class="company-name">
                      Компания не найдена
                    </span>

                    <span
                      v-if="vacancy.company_id"
                      class="company-site"
                    >
                      {{ vacancy.company_id }}
                    </span>
                  </div>
                </div>
  
                <p class="vacancy-description">
                  {{ vacancy.info || "Описание вакансии отсутствует." }}
                </p>
  
                <!-- Tags -->
  
                <div
                  v-if="normalizedHashtags(vacancy).length"
                  class="vacancy-tags"
                >
                  <span
                    v-for="tag in normalizedHashtags(vacancy)"
                    :key="tag"
                    class="vacancy-tag"
                  >
                    #{{ tag }}
                  </span>
                </div>
  
                <!-- Footer -->
  
                <div class="vacancy-footer">
                  <div class="vacancy-id">
                    ID:
                    <span>
                      {{ vacancy.id }}
                    </span>
                  </div>
  
                  <button
                    type="button"
                    class="approve-button"
                    :disabled="approvingVacancyId === vacancy.id"
                    @click="approveVacancy(vacancy)"
                  >
                    <template
                      v-if="approvingVacancyId === vacancy.id"
                    >
                      <span class="button-spinner"></span>
  
                      Публикуем...
                    </template>
  
                    <template v-else>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12.5L9.5 17L19 7.5"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
  
                      Опубликовать
                    </template>
                  </button>
                </div>
              </div>
            </article>
          </section>
        </div>
      </main>
  
      <Footer />
  
      <!-- =====================================================
           Snackbar
      ====================================================== -->
  
      <Transition name="snackbar">
        <div
          v-if="showSnackbar"
          class="snackbar"
        >
          <div class="snackbar-icon">
            ✓
          </div>
  
          <span>
            {{ snackbarMessage }}
          </span>
        </div>
      </Transition>
    </div>
  </template>
  
  <script setup>
  import {
    onMounted,
    ref
  } from "vue";

  import {
  waitForAuthReady
} from "../utils/auth.js";
  
  import DefaultHeader
    from "../components/DefaultHeader.vue";
  
  import Footer
    from "../components/MiniFooter.vue";
  
  import AdminService
    from "../api/AdminService";

  const isAccessChecked =
    ref(false);

  const isAdmin =
    ref(false);
  
  const pendingVacancies = ref([]);
  
  const isLoading = ref(true);
  const approvingVacancyId = ref(null);
  
  const errorMessage = ref("");
  
  const showSnackbar = ref(false);
  const snackbarMessage = ref("");
  
  onMounted(async () => {
  try {
    /*
     * Ждём, пока Firebase восстановит сессию.
     */
    const user =
      await waitForAuthReady();

    /*
     * Пользователь не авторизован.
     */
    if (!user) {
      console.warn(
        "❌ PendingVacancies: пользователь не авторизован"
      );

      window.location.replace(
        "https://hr3.space/"
      );

      return;
    }

    /*
     * Проверяем admin flag.
     */
    const adminFlag =
      localStorage.getItem(
        "is_admin"
      ) === "true";

    if (!adminFlag) {
      console.warn(
        "⛔ PendingVacancies: нет прав администратора"
      );

      window.location.replace(
        "https://hr3.space/"
      );

      return;
    }

    /*
     * Только теперь разрешаем показать страницу.
     */
    isAdmin.value =
      true;

    /*
     * И только после проверки идём в AdminService.
     */
    await loadPendingVacancies();

  } catch (error) {
    console.error(
      "❌ Ошибка проверки доступа PendingVacancies:",
      error
    );

    window.location.replace(
      "https://hr3.space/"
    );

  } finally {
    isAccessChecked.value =
      true;
  }
});
  
  async function loadPendingVacancies() {
    isLoading.value = true;
    errorMessage.value = "";
  
    try {
      pendingVacancies.value =
        await AdminService.getPendingVacancies();
  
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error
        || error.message
        || "Произошла неизвестная ошибка";
  
    } finally {
      isLoading.value = false;
    }
  }
  
  async function approveVacancy(
    vacancy
  ) {
    if (
      approvingVacancyId.value
    ) {
      return;
    }
  
    approvingVacancyId.value =
      vacancy.id;
  
    try {
      await AdminService
        .approvePendingVacancy(
          vacancy.id
        );
  
      pendingVacancies.value =
        pendingVacancies.value.filter(
          item =>
            item.id !== vacancy.id
        );
  
      showSuccessSnackbar(
        `Вакансия «${vacancy.title || "Без названия"}» опубликована`
      );
  
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error
        || error.message
        || "Не удалось опубликовать вакансию";
  
    } finally {
      approvingVacancyId.value =
        null;
    }
  }
  
  function normalizedHashtags(
    vacancy
  ) {
    const hashtags =
      vacancy.hashtags || [];
  
    return hashtags
      .map(tag => {
        if (
          typeof tag === "string"
        ) {
          return tag;
        }
  
        return tag?.name || "";
      })
      .filter(Boolean)
      .slice(0, 8);
  }
  
  function locationTitle(
    vacancy
  ) {
    if (
      vacancy.is_remote
    ) {
      return "Удалённо";
    }
  
    if (
      vacancy.city
    ) {
      return vacancy.city;
    }
  
    if (
      vacancy.street
    ) {
      return vacancy.street;
    }
  
    return "Локация не указана";
  }
  
  function formatDate(
    timestamp
  ) {
    if (!timestamp) {
      return "";
    }
  
    const number =
      Number(timestamp);
  
    if (
      Number.isNaN(number)
    ) {
      return "";
    }
  
    const date =
      new Date(
        number * 1000
      );
  
    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        day: "2-digit",
        month: "short",
        year: "numeric"
      }
    ).format(date);
  }
  
  function showSuccessSnackbar(
    message
  ) {
    snackbarMessage.value =
      message;
  
    showSnackbar.value =
      true;
  
    window.setTimeout(
      () => {
        showSnackbar.value =
          false;
      },
      3000
    );
  }


  function companyInitial(name) {
  if (!name) {
    return "?";
  }

  return name
    .trim()
    .charAt(0)
    .toUpperCase();
}

function formatCompanySite(site) {
  if (!site) {
    return "";
  }

  return site
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "");
}

  </script>
  
  <style scoped>
  * {
    box-sizing: border-box;
  }
  
  /* =========================================================
     PAGE
  ========================================================= */
  
  .pending-page {
    min-height: 100dvh;
  
    display: flex;
    flex-direction: column;
  
    color: #24272d;
  
    background:
      #f4f6f9;
  }
  
  .pending-content {
    flex: 1;
  
    padding:
      54px
      24px
      80px;
  
    background:
      radial-gradient(
        circle at 15% 0%,
        rgba(243, 167, 59, 0.08),
        transparent 340px
      ),
      radial-gradient(
        circle at 90% 10%,
        rgba(219, 103, 84, 0.055),
        transparent 320px
      ),
      #f4f6f9;
  }
  
  .pending-container {
    width: 100%;
    max-width: 1180px;
  
    margin: 0 auto;
  }
  
  /* =========================================================
     HEADING
  ========================================================= */
  
  .pending-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  
    gap: 30px;
  
    margin-bottom: 32px;
  }
  
  .pending-label {
    display: block;
  
    margin-bottom: 6px;
  
    color: #999fa9;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 10px;
  
    letter-spacing: 0.09em;
  
    text-transform: uppercase;
  }
  
  .pending-heading h1 {
    margin: 0;
  
    color: #22262d;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 30px;
    line-height: 1.2;
  }
  
  .pending-heading p {
    max-width: 560px;
  
    margin:
      9px
      0
      0;
  
    color: #7c838e;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 13px;
    line-height: 1.55;
  }
  
  .pending-counter {
    display: flex;
    align-items: center;
  
    gap: 7px;
  
    padding:
      9px
      13px;
  
    color: #a36a25;
  
    background:
      rgba(
        243,
        167,
        59,
        0.10
      );
  
    border:
      1px solid
      rgba(
        243,
        167,
        59,
        0.18
      );
  
    border-radius: 12px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 12px;
  }
  
  .counter-dot {
    width: 7px;
    height: 7px;
  
    border-radius: 50%;
  
    background:
      #ed9c38;
  
    box-shadow:
      0 0 0 4px
      rgba(
        237,
        156,
        56,
        0.12
      );
  }
  
  .counter-caption {
    opacity: 0.72;
  }
  
  /* =========================================================
     GRID
  ========================================================= */
  
  .vacancies-grid {
    display: grid;
  
    grid-template-columns:
      repeat(
        2,
        minmax(
          0,
          1fr
        )
      );
  
    gap: 20px;
  }
  
  /* =========================================================
     CARD
  ========================================================= */
  
  .vacancy-card {
    min-width: 0;
  
    overflow: hidden;
  
    background:
      #ffffff;
  
    border:
      1px solid
      rgba(
        30,
        43,
        61,
        0.07
      );
  
    border-radius: 22px;
  
    box-shadow:
      0 10px 30px
      rgba(
        15,
        23,
        42,
        0.05
      );
  
    transition:
      transform
      0.22s ease,
      border-color
      0.22s ease,
      box-shadow
      0.22s ease;
  }
  
  .vacancy-card:hover {
    transform:
      translateY(-3px);
  
    border-color:
      rgba(
        230,
        145,
        47,
        0.18
      );
  
    box-shadow:
      0 18px 42px
      rgba(
        45,
        51,
        65,
        0.10
      );
  }
  
  /* =========================================================
     IMAGE
  ========================================================= */
  
  .vacancy-image-wrap {
    position: relative;
  
    height: 210px;
  
    overflow: hidden;
  
    background:
      #eef1f5;
  }
  
  .vacancy-image {
    width: 100%;
    height: 100%;
  
    display: block;
  
    object-fit: cover;
  }
  
  .vacancy-image-placeholder {
    width: 100%;
    height: 100%;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: #b5bac3;
  
    background:
      linear-gradient(
        135deg,
        #f0f2f5,
        #e5e9ef
      );
  }
  
  .vacancy-image-placeholder svg {
    width: 42px;
    height: 42px;
  }
  
  .moderation-badge {
    position: absolute;
  
    top: 16px;
    left: 16px;
  
    padding:
      8px
      11px;
  
    color:
      #ffffff;
  
    background:
      rgba(
        31,
        35,
        42,
        0.82
      );
  
    border:
      1px solid
      rgba(
        255,
        255,
        255,
        0.15
      );
  
    border-radius: 10px;
  
    backdrop-filter:
      blur(14px);
  
    -webkit-backdrop-filter:
      blur(14px);
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 10px;
  }
  
  /* =========================================================
     BODY
  ========================================================= */
  
  .vacancy-body {
    padding:
      21px
      21px
      18px;
  }
  
  .vacancy-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
  
    gap: 20px;
  
    margin-bottom: 7px;
  }
  
  .vacancy-category {
    min-width: 0;
  
    overflow: hidden;
  
    color: #8c939e;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 9px;
  
    letter-spacing: 0.05em;
  
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
  }
  
  .vacancy-date {
    flex-shrink: 0;
  
    color: #a3a8b0;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 9px;
  }
  
  .vacancy-title {
    margin: 0;
  
    color: #25292f;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 20px;
    line-height: 1.3;
  }
  
  /* =========================================================
     META
  ========================================================= */
  
  .vacancy-meta {
    display: flex;
    flex-wrap: wrap;
  
    gap:
      8px
      14px;
  
    margin-top: 14px;
  }
  
  .meta-item {
    min-width: 0;
  
    display: flex;
    align-items: center;
  
    gap: 5px;
  
    color: #7f8792;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 10px;
  }
  
  .meta-item svg {
    width: 14px;
    height: 14px;
  
    flex-shrink: 0;
  }
  
  .meta-item span {
    overflow: hidden;
  
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  /* =========================================================
     DESCRIPTION
  ========================================================= */
  
  .vacancy-description {
    display: -webkit-box;
  
    margin:
      17px
      0
      0;
  
    overflow: hidden;
  
    color: #676f7a;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 12px;
    line-height: 1.6;
  
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
  }
  
  /* =========================================================
     TAGS
  ========================================================= */
  
  .vacancy-tags {
    display: flex;
    flex-wrap: wrap;
  
    gap: 7px;
  
    margin-top: 16px;
  }
  
  .vacancy-tag {
    max-width: 100%;
  
    overflow: hidden;
  
    padding:
      6px
      9px;
  
    color: #667080;
  
    background:
      #f3f5f7;
  
    border:
      1px solid
      rgba(
        31,
        44,
        62,
        0.055
      );
  
    border-radius: 9px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 9px;
  
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  /* =========================================================
     FOOTER
  ========================================================= */
  
  .vacancy-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  
    gap: 14px;
  
    margin-top: 20px;
  
    padding-top: 17px;
  
    border-top:
      1px solid
      rgba(
        31,
        44,
        62,
        0.065
      );
  }
  
  .vacancy-id {
    min-width: 0;
  
    overflow: hidden;
  
    color: #a1a6ae;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 8px;
  
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .vacancy-id span {
    color: #858c96;
  }
  
  .approve-button {
    min-width: 145px;
    height: 42px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    gap: 8px;
  
    padding:
      0
      17px;
  
    cursor: pointer;
  
    color: #ffffff;
  
    background:
      linear-gradient(
        135deg,
        #42b878,
        #2ea46a
      );
  
    border: 0;
  
    border-radius: 12px;
  
    box-shadow:
      0 8px 18px
      rgba(
        46,
        164,
        106,
        0.18
      );
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 11px;
  
    transition:
      transform
      0.18s ease,
      box-shadow
      0.18s ease,
      opacity
      0.18s ease;
  }
  
  .approve-button:hover:not(:disabled) {
    transform:
      translateY(-1px);
  
    box-shadow:
      0 11px 22px
      rgba(
        46,
        164,
        106,
        0.24
      );
  }
  
  .approve-button:disabled {
    cursor: default;
  
    opacity: 0.72;
  }
  
  .approve-button svg {
    width: 16px;
    height: 16px;
  }
  
  /* =========================================================
     BUTTON SPINNER
  ========================================================= */
  
  .button-spinner {
    width: 14px;
    height: 14px;
  
    border:
      2px solid
      rgba(
        255,
        255,
        255,
        0.35
      );
  
    border-top-color:
      #ffffff;
  
    border-radius: 50%;
  
    animation:
      button-spin
      0.75s
      linear
      infinite;
  }
  
  @keyframes button-spin {
    to {
      transform:
        rotate(360deg);
    }
  }
  
  /* =========================================================
     STATES
  ========================================================= */
  
  .state-card {
    max-width: 520px;
  
    margin:
      80px
      auto;
  
    padding:
      42px
      34px;
  
    text-align: center;
  
    background:
      #ffffff;
  
    border:
      1px solid
      rgba(
        31,
        44,
        62,
        0.07
      );
  
    border-radius: 22px;
  
    box-shadow:
      0 14px 38px
      rgba(
        15,
        23,
        42,
        0.055
      );
  }
  
  .state-icon {
    width: 52px;
    height: 52px;
  
    margin:
      0
      auto
      17px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: #ffffff;
  
    border-radius: 16px;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 20px;
  }
  
  .state-icon.success {
    background:
      linear-gradient(
        135deg,
        #44be7d,
        #2fa86c
      );
  }
  
  .state-icon.error {
    background:
      linear-gradient(
        135deg,
        #e76b68,
        #ca4d54
      );
  }
  
  .state-card h2 {
    margin: 0;
  
    color: #282c33;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 18px;
  }
  
  .state-card p {
    margin:
      9px
      0
      0;
  
    color: #848b95;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 12px;
    line-height: 1.55;
  }
  
  .state-button {
    margin-top: 20px;
  
    padding:
      11px
      16px;
  
    cursor: pointer;
  
    color: #ffffff;
  
    background:
      #347fe7;
  
    border: 0;
  
    border-radius: 11px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 11px;
  }
  
  /* =========================================================
     SKELETON
  ========================================================= */
  
  .skeleton-card {
    pointer-events: none;
  }
  
  .skeleton-image,
  .skeleton-line,
  .skeleton-tag {
    background:
      linear-gradient(
        90deg,
        #edf0f3 25%,
        #f6f7f9 38%,
        #edf0f3 63%
      );
  
    background-size:
      400% 100%;
  
    animation:
      skeleton-shimmer
      1.4s
      ease
      infinite;
  }
  
  .skeleton-image {
    height: 210px;
  }
  
  .skeleton-content {
    padding: 22px;
  }
  
  .skeleton-line {
    height: 12px;
  
    margin-bottom: 10px;
  
    border-radius: 6px;
  }
  
  .skeleton-line.small {
    width: 30%;
    height: 8px;
  }
  
  .skeleton-line.title {
    width: 72%;
    height: 20px;
  }
  
  .skeleton-line.short {
    width: 55%;
  }
  
  .skeleton-tags {
    display: flex;
  
    gap: 8px;
  
    margin-top: 18px;
  }
  
  .skeleton-tag {
    width: 72px;
    height: 24px;
  
    border-radius: 8px;
  }
  
  @keyframes skeleton-shimmer {
    0% {
      background-position:
        100% 0;
    }
  
    100% {
      background-position:
        -100% 0;
    }
  }
  
  /* =========================================================
     SNACKBAR
  ========================================================= */
  
  .snackbar {
    position: fixed;
  
    left: 50%;
    bottom: 34px;
  
    z-index: 99999;
  
    min-width: 300px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    gap: 9px;
  
    padding:
      12px
      18px;
  
    color: #ffffff;
  
    background:
      rgba(
        30,
        35,
        42,
        0.95
      );
  
    border:
      1px solid
      rgba(
        255,
        255,
        255,
        0.08
      );
  
    border-radius: 14px;
  
    box-shadow:
      0 16px 38px
      rgba(
        0,
        0,
        0,
        0.22
      );
  
    backdrop-filter:
      blur(16px);
  
    -webkit-backdrop-filter:
      blur(16px);
  
    transform:
      translateX(-50%);
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 12px;
  }
  
  .snackbar-icon {
    width: 24px;
    height: 24px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: #ffffff;
  
    background:
      #3dbf78;
  
    border-radius: 50%;
  
    font-size: 11px;
  }
  
  .snackbar-enter-active,
  .snackbar-leave-active {
    transition:
      opacity
      0.2s ease,
      transform
      0.22s ease;
  }
  
  .snackbar-enter-from,
  .snackbar-leave-to {
    opacity: 0;
  
    transform:
      translateX(-50%)
      translateY(10px);
  }
  
  /* =========================================================
     DARK
  ========================================================= */
  
  html[data-theme="dark"]
  .pending-page {
    color: #f2f2f4;
  
    background:
      #18191c;
  }
  
  html[data-theme="dark"]
  .pending-content {
    background:
      radial-gradient(
        circle at 15% 0%,
        rgba(243, 167, 59, 0.06),
        transparent 340px
      ),
      radial-gradient(
        circle at 90% 10%,
        rgba(219, 103, 84, 0.05),
        transparent 320px
      ),
      #18191c;
  }
  
  html[data-theme="dark"]
  .pending-heading h1 {
    color: #f2f3f5;
  }
  
  html[data-theme="dark"]
  .pending-heading p {
    color: #939aa5;
  }
  
  html[data-theme="dark"]
  .pending-label {
    color: #747b85;
  }
  
  html[data-theme="dark"]
  .pending-counter {
    color: #f2bd78;
  
    background:
      rgba(
        243,
        167,
        59,
        0.09
      );
  
    border-color:
      rgba(
        243,
        167,
        59,
        0.16
      );
  }
  
  html[data-theme="dark"]
  .vacancy-card,
  html[data-theme="dark"]
  .state-card {
    background:
      #232428;
  
    border-color:
      rgba(
        255,
        255,
        255,
        0.065
      );
  
    box-shadow:
      0 12px 30px
      rgba(
        0,
        0,
        0,
        0.20
      );
  }
  
  html[data-theme="dark"]
  .vacancy-card:hover {
    border-color:
      rgba(
        238,
        165,
        75,
        0.17
      );
  
    box-shadow:
      0 18px 44px
      rgba(
        0,
        0,
        0,
        0.32
      );
  }
  
  html[data-theme="dark"]
  .vacancy-image-wrap {
    background:
      #2d2f34;
  }
  
  html[data-theme="dark"]
  .vacancy-image-placeholder {
    color: #686e78;
  
    background:
      linear-gradient(
        135deg,
        #292b30,
        #33363c
      );
  }
  
  html[data-theme="dark"]
  .vacancy-title,
  html[data-theme="dark"]
  .state-card h2 {
    color: #f1f2f4;
  }
  
  html[data-theme="dark"]
  .vacancy-description {
    color: #a0a6af;
  }
  
  html[data-theme="dark"]
  .vacancy-category,
  html[data-theme="dark"]
  .vacancy-date,
  html[data-theme="dark"]
  .meta-item {
    color: #8e949e;
  }
  
  html[data-theme="dark"]
  .vacancy-tag {
    color: #aab0b8;
  
    background:
      #303238;
  
    border-color:
      rgba(
        255,
        255,
        255,
        0.05
      );
  }
  
  html[data-theme="dark"]
  .vacancy-footer {
    border-top-color:
      rgba(
        255,
        255,
        255,
        0.07
      );
  }
  
  html[data-theme="dark"]
  .skeleton-image,
  html[data-theme="dark"]
  .skeleton-line,
  html[data-theme="dark"]
  .skeleton-tag {
    background:
      linear-gradient(
        90deg,
        #292b30 25%,
        #34363c 38%,
        #292b30 63%
      );
  
    background-size:
      400% 100%;
  }
  
  /* =========================================================
     TABLET
  ========================================================= */
  
  @media (
    max-width: 900px
  ) {
    .pending-content {
      padding:
        40px
        18px
        70px;
    }
  
    .vacancies-grid {
      grid-template-columns:
        1fr;
    }
  }
  
  /* =========================================================
     MOBILE
  ========================================================= */
  
  @media (
    max-width: 600px
  ) {
    .pending-content {
      padding:
        30px
        14px
        60px;
    }
  
    .pending-heading {
      flex-direction:
        column;
  
      gap: 16px;
  
      margin-bottom:
        24px;
    }
  
    .pending-heading h1 {
      font-size:
        25px;
    }
  
    .pending-heading p {
      font-size:
        12px;
    }
  
    .vacancy-card {
      border-radius:
        18px;
    }
  
    .vacancy-image-wrap,
    .skeleton-image {
      height:
        180px;
    }
  
    .vacancy-body {
      padding:
        18px
        16px
        16px;
    }
  
    .vacancy-title {
      font-size:
        17px;
    }
  
    .vacancy-footer {
      flex-direction:
        column;
  
      align-items:
        stretch;
    }
  
    .approve-button {
      width: 100%;
    }
  
    .vacancy-id {
      order: 2;
  
      text-align:
        center;
    }
  
    .snackbar {
      width:
        calc(
          100% - 28px
        );
  
      min-width: 0;
    }
  }
  
  /* =========================================================
     REDUCED MOTION
  ========================================================= */
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
    .vacancy-card,
    .approve-button,
    .snackbar-enter-active,
    .snackbar-leave-active {
      transition: none;
    }
  
    .skeleton-image,
    .skeleton-line,
    .skeleton-tag,
    .button-spinner {
      animation: none;
    }
  }

  /* =========================================================
   COMPANY
========================================================= */

.company-card {
  display: flex;
  align-items: center;

  gap: 11px;

  margin-top: 16px;

  padding:
    11px
    12px;

  color: inherit;
  text-decoration: none;

  background:
    #f7f8fa;

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.06
    );

  border-radius: 14px;

  transition:
    background-color
    0.18s ease,
    border-color
    0.18s ease,
    transform
    0.18s ease,
    box-shadow
    0.18s ease;
}

.company-card:hover {
  transform:
    translateY(-1px);

  background:
    #f4f7fb;

  border-color:
    rgba(
      53,
      132,
      247,
      0.16
    );

  box-shadow:
    0 7px 18px
    rgba(
      31,
      64,
      110,
      0.06
    );
}

.company-logo {
  width: 42px;
  height: 42px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  color: #5876a3;

  background:
    linear-gradient(
      135deg,
      #e8eef7,
      #edf1f6
    );

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.05
    );

  border-radius: 12px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 15px;
}

.company-logo img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}

.company-logo.missing {
  color: #9da4ae;

  background:
    #eceff3;
}

.company-content {
  min-width: 0;

  flex: 1;

  display: flex;
  flex-direction: column;

  gap: 2px;
}

.company-label {
  color: #a0a6af;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}

.company-name {
  overflow: hidden;

  color: #343941;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 12px;
  line-height: 1.3;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-site {
  overflow: hidden;

  color: #89919d;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-open {
  width: 30px;
  height: 30px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #9ba3ae;

  background:
    #ffffff;

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.055
    );

  border-radius: 9px;

  transition:
    color
    0.18s ease,
    background-color
    0.18s ease,
    transform
    0.18s ease;
}

.company-open svg {
  width: 15px;
  height: 15px;
}

.company-card:hover
.company-open {
  color: #ffffff;

  background:
    #4387ed;

  transform:
    translateX(2px);
}

.company-card-missing {
  pointer-events: none;

  opacity: 0.7;
}

  </style>